<template>
  <v-hover>
    <v-card slot-scope="{ hover }" tile class="home-card">
      <template v-if="homeType === 'favorite'">
        <template v-if="isAddedByAutoSearch()">
          <div class="corner-angle corner-angle-auto-search" />
          <div class="tilted-text">
            <span>Auto</span>
            <br>Added
          </div>
        </template>
        <template v-else-if="isAddedByAgent()">
          <div class="corner-angle" />
          <div class="tilted-text">
            <span>Agent</span>
            <br>Added
          </div>
        </template>
      </template>
      <v-img
        :src="propertyImage()"
        :aspect-ratio="16 / 10"
        @click.stop="isMobile ? $emit('card-view-detail', $event) : null"
      >
        <template v-if="showProgress" v-slot:placeholder>
          <v-layout fill-height align-center justify-center ma-0>
            <v-progress-circular indeterminate color="grey darken-1" />
          </v-layout>
        </template>
        <!-- Hover element that shows the additional options for a home listing. -->
        <v-fade-transition>
          <v-layout
            v-if="!isMobile && (hover || modalShowing)"
            wrap
            align-center
            justify-start
            class="text-xs-left card-favorite__image-overlay py-3"
          >
            <div class="hover-options-wrapper">
              <slot name="hoverCard" :propertyInfo="propertyInfo" />
            </div>
          </v-layout>
        </v-fade-transition>
      </v-img>
      <v-card-title class="text-xs-left card-result-title">
        <v-layout wrap>
          <v-flex xs12 class="property-name">
            {{ propertyInfo["Address"] }}, {{ propertyInfo["City"] }},
            {{ propertyInfo["StateOrProvince"] }} {{ propertyInfo["PostalCode"] }}
          </v-flex>
          <v-flex v-if="isMobile" class="text-xs-center">
            ${{ propertyInfo["ListPrice"] | commaSeparated }}
          </v-flex>

          <template v-if="!isMobile">
            <v-flex
              class="home-status"
              :class="{
                'not-request': hideStatuses.includes(propertyInfo.Status)
              }"
            >
              {{ propertyInfo.Status }}
            </v-flex>
            <v-flex class="text-xs-right">
              <span
                v-if="homeType === 'favorite' && isTourRequested"
                class="pr-2"
              >
                <v-icon class="green--text" small>
                  fas fa-paper-plane
                </v-icon>
              </span>
              ${{ propertyInfo["ListPrice"] | commaSeparated }}
            </v-flex>
          </template>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <table v-if="!isMobile" class="card-result-table">
          <tr v-for="row in cardPropertyParams" :key="row.key">
            <td class="text-xs-left table-key">
              {{ row.key }}
            </td>
            <td class="text-xs-right table-value">
              {{ row.value }}
            </td>
          </tr>
        </table>

        <!-- For Toured Properties Only -->
        <div
          v-if="propertyType === 'toured' && !isMobile"
          class="leave-feedback"
          @click.stop="openFeedbackFillupModal()"
        >
          <span :class="['feedback-text', getMatchPercentClass()]">{{
            getFeedbackValue()
          }}</span>
        </div>
        <v-layout v-if="propertyType === 'toured' && isMobile">
          <v-flex>
            <div
              class="leave-feedback"
              @click.stop="openFeedbackFillupMobileModal($event)"
            >
              <span :class="['feedback-text', getMatchPercentClass()]">{{
                getFeedbackValue()
              }}</span>
            </div>
          </v-flex>
        </v-layout>
        <v-layout
          v-if="
            isMobile &&
              profileStatus === 'agent' &&
              searchObject.searchType !== 'sold'"
        >
          <v-flex @click.stop="$emit('card-new-assign')">
            <span class="view-details">Assign to Client</span>
          </v-flex>
          <v-flex @click.stop="$emit('card-new-request-tour')">
            <span class="view-details">Request Tour</span>
          </v-flex>
        </v-layout>
        <v-layout v-if="isMobile && profileStatus === 'client'">
          <v-flex
            v-if="propertyType !== 'toured'"
            :class="{'standard-text' : isTourRequested}"
          >
            <v-icon
              :class="isFav ? 'hwayzSun' : 'hwayzLightGray'"
              small
              @click.stop="
                isFav
                  ? $emit('card-un-set-favorite')
                  : $emit('card-set-favorite')"
            >
              fas fa-heart
            </v-icon>
          </v-flex>
          <v-flex v-if="isFav">
            <v-icon v-if="isTourRequested" class="green--text standard-text" small>
              fas fa-paper-plane
            </v-icon>
            <v-icon
              v-else
              :color="colors.hwayzGray"
              small
              @click.stop="$emit('card-request-tour')"
            >
              fas fa-paper-plane
            </v-icon>
          </v-flex>
        </v-layout>
      </v-card-text>
      <template v-if="clientData && selectedFeedbackData">
        <template v-if="!isMobile">
          <v-dialog
            v-model="displayFeedbackModal"
            :width="modal.xlarge_width"
            :max-width="modal.xlarge_maxWidth"
            persistent
          >
            <feedback-component
              :property-data="selectedProperty"
              :feedback-data="selectedFeedbackData"
              @on-feedback-updated="updateFeedback($event)"
              @close="closeFeedbackFillupModal"
            />
          </v-dialog>
        </template>
        <template v-if="showMobileFeedbackModal">
          <feedback-component
            class="mobile-feedback-component"
            :property-data="selectedProperty"
            :feedback-data="selectedFeedbackData"
            @on-feedback-updated="updateFeedback($event)"
            @close="closeFeedbackFillupModal"
          />
        </template>
      </template>
    </v-card>
  </v-hover>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import {
  calculateFeedbackMatch,
  getPropertyImage,
  isPropertyAClientFavorite,
  getClientFavFromPropertyId
} from '../../utils/propertyUtils'
import FeedbackComponent from '../feedback/FeedbackComponent'
import { isEmpty } from '../../utils/generalUtils'
import colors from '../../scss/_colors.scss'
import { HIDE_STATUSES } from '../common/searchPropertyConst'
import refreshClientData from '../mixins/refreshClientData'

export default {
  components: { FeedbackComponent },

  filters: {
    commaSeparated(val) {
      return (val || '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },

  mixins: [refreshClientData],

  props: {
    propertyInfo: {
      type: Object,
      required: true
    },
    homeType: {
      type: String,
      required: true
    },
    propertyType: {
      type: String,
      default: 'favorites'
    },
    isTourRequested: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      colors,
      modalShowing: false,
      showProgress: true,
      displayFeedbackModal: false,
      selectedFeedbackData: null,
      selectedProperty: null,
      hideStatuses: HIDE_STATUSES
    }
  },

  computed: {
    ...mapState('client', ['associatedAgent']),
    ...mapState('common', [
      'isMobile',
      'searchObject',
      'showMobileFeedbackModal',
      'selectedHome'
    ]),
    ...mapState('style', ['modal']),

    cardPropertyParams() {
      return [
        { key: 'Type', value: this.propertyInfo.PropertyType },
        {
          key: 'Size',
          value: `${this.$options.filters.commaSeparated(
            this.propertyInfo.LivingArea
          )} SqFt`
        },
        {
          key: 'Rooms',
          value: `${this.propertyInfo.BedroomsTotal} Beds + ${this.propertyInfo.BathroomsFull}/${this.propertyInfo.BathroomsHalf || 0} Baths`
        }
      ]
    },

    // Check if a property is part of client favorite list
    isFav() {
      return isPropertyAClientFavorite(this.propertyInfo, this.clientFavorites)
    }
  },
  destroyed() {
    this.setShowingMobileBackButton(false)
    this.closeMobileFeedbackFillupModal(false)
  },

  methods: {
    ...mapMutations('common', {
      setPersonName: 'SET_PERSON_NAME',
      setSelectedHome: 'SET_SELECTED_HOME',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL'
    }),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    ...mapMutations('client', {
      setClientFavorites: 'SAVE_CLIENT_FAVORITES'
    }),

    isAddedByAgent() {
      if (this.clientData && this.clientData.homes) {
        const homeObject = this.clientData.homes.find(
          home => home.propertyId === this.propertyInfo._id
        )
        return homeObject && homeObject._creator !== this.clientData._id && homeObject._creator !== 'autosearch'
      }
    },

    isAddedByAutoSearch() {
      return this.propertyInfo.autoSearch
    },

    propertyImage() {
      return getPropertyImage(this.propertyInfo)
    },

    getFeedbackValue() {
      const clientHome = this.getPropertyObject()
      if (!isEmpty(clientHome.feedback)) {
        const percentValue = Math.round(
          calculateFeedbackMatch(clientHome.feedback.match)
        )
        if (percentValue > 0) return percentValue + '%'
      }
      return 'Leave Feedback'
    },

    getMatchPercentClass() {
      const clientHome = this.getPropertyObject()
      let percentValue = 0
      if (!isEmpty(clientHome.feedback)) {
        percentValue = Math.round(
          calculateFeedbackMatch(clientHome.feedback.match)
        )
      }
      let percentClass = ''
      if (percentValue / 10 >= 8) {
        percentClass = 'hwayzGreen'
      } else if (percentValue / 10 >= 6) {
        percentClass = 'hwayzSun'
      } else if (percentValue / 10 > 0) {
        percentClass = 'color-grey'
      }
      return !isEmpty(percentClass) ? [percentClass, 'hover-back'] : null
    },

    async openFeedbackFillupModal() {
      if (this.clientData && this.clientData.homePrefs) {
        this.displayFeedbackModal = true
        const property = this.getPropertyObject()
        this.selectedFeedbackData = {
          feedback: property.feedback,
          _id: property.favoriteId
        }
        this.selectedProperty = property
      }
    },

    updateFeedback(data) {
      this.selectedFeedbackData = data
    },

    async openFeedbackFillupMobileModal() {
      const propertyObject = await this.getPropertyObject()
      this.openFeedbackFillupModal()
      this.setSelectedHome(propertyObject)
      this.setPersonName(this.clientData.name)
      this.setShowingMobileBackButton(true)
      this.setShowMobileFeedbackModal(true)
      this.displayFeedbackModal = true
    },

    async closeMobileFeedbackFillupModal(updatedFeedback) {
      if (updatedFeedback) {
        const propertyObject = this.getPropertyObject()
        this.updateSingleFavorite({ ...propertyObject, ...updatedFeedback, _id: propertyObject._id })
      }
      this.setShowMobileFeedbackModal(false)
      this.setSelectedHome(null)
    },

    async closeFeedbackFillupModal(updatedFeedback) {
      const propertyObject = this.getPropertyObject()
      this.displayFeedbackModal = false
      this.selectedFeedbackData = null
      this.updateSingleFavorite({ ...propertyObject, ...updatedFeedback, _id: propertyObject._id })
    },

    getPropertyObject() {
      return !this.propertyInfo.favoriteId
        ? getClientFavFromPropertyId(
          this.clientFavorites,
          this.propertyInfo._id
        )
        : this.propertyInfo
    }
  }
}
</script>

<style lang="sass">
.corner-angle
  background-color: transparent
  position: absolute
  z-index: 1
  &.corner-angle-auto-search
    &::after
      border-left-color: $hwayzYellow
      border-top-color: $hwayzYellow
  &::before, &::after
    content: ''
    position: absolute
    top: 0
    left: 0
    border-color: transparent
    border-style: solid
  &::after
    border-width: 4.4rem
    border-left-color: $hwayzPinkOrange
    border-top-color: $hwayzPinkOrange
    z-index: 17

.tilted-text
  position: absolute
  top: .7rem
  left: .7rem
  color: $hwayzBorder
  font-size: 1.3rem
  z-index: 2
  text-align: center
  transform: rotate(-45deg)
</style>

<style lang="sass" scoped>
.mobile-feedback-modal-wrapper
  position: fixed
  left: 0
  top: 0
  width: 100%
  height: 100%
  overflow: scroll
  z-index: 4
  padding-top: 48px

.view-details
  color: $hwayzYellow
  font-size: 2rem
.request-tour
  color: #4A4949
.tour-requested
  color: $accept

.home-status
  text-align: left
  &.not-request
    color: $hwayzYellow

.hover-options-wrapper
  padding: 1rem 0 !important
  width: 100%

.card-result-title
  background-color: $hwayzBlack
  color: $hwayzWhite
  font-weight: 300
  font-size: 1.3rem
  display: flex
  justify-content: flex-start
  align-content: flex-start
  min-height: 7rem
  position: relative
  .property-name
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipses
    margin-bottom: 0.5rem

.card-result-table
  width: 100%

.card-result-table td
  padding: 3px

.table-key
  color: $hwayzBlack

.table-value
  color: $black

.image-overlay-label
  background-color: $hwayzBlack
  margin: 10px 20px

.card-favorite__image-overlay
  position: absolute
  bottom: 0
  top: 0
  left: 0
  right: 0
  margin: 0 !important
  background-color: $hwayzBlack
  opacity: 0.9
  color: $hwayzWhite
  padding-left: 20%

.result-overlay-icon
  font-size: 1.8rem
  margin-right: 1rem

.fill-width
  width: 100%

.leave-feedback
  margin-top: 1rem
  cursor: pointer

.feedback-text
  font-size: 1.6rem
  color: $hwayzBrandGold
  display: block
  line-height: 36px
  margin: 0 auto
  &:hover
    color: $hwayzBrandBlue

.hover-back
  text-align: center
  border-radius: 50%
  width: 36px
  height: 36px
  line-height: 36px

.color-grey
  color: $hwayzMediumGray !important
  &.hover-back:hover
    background-color: rgba($hwayzMediumGray, .2)

.hwayzLightGray
  color: $hwayzLightGray !important

.hwayzGreen
  color: $hwayzGreen !important
  &.hover-back:hover
    background-color: rgba($hwayzGreen, .2)

.hwayzSun
  color: $hwayzSun !important
  &.hover-back:hover
    background-color: rgba($hwayzSun, .2)

.disable-icon
  @extend %disabled-state

@media only screen and (max-width: 599px)
  .card-result
    border-radius: 10px

  .card-result-title
    text-align: center !important
    font-size: 1.6rem
  .v-icon
    font-size: 3rem !important
  .feedback-text
    font-size: 2.5rem !important
  .v-card__text
    display: flex
    flex-direction: row
    justify-content: center
    align-items: center
  .leave-feedback
    margin-top: 0rem
</style>
